import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshLambertMaterial,
  SphereGeometry,
  type BufferGeometry,
  type Material,
  type Object3D,
} from 'three';
import { LIVERY } from '../themes/livery';

/**
 * The comic crash of issue #67 (GDD §3.4): grey smoke, a simple pilot with a helmet and a parachute. Hidden
 * outside DERRIBO. No fire and no blood: nobody dies on screen. The three groups live in the scene, not
 * under the plane, because the plane keeps falling and rotating while the pilot floats down.
 */
export interface CrashFx {
  smoke: Group;
  pilot: Group;
  parachute: Group;
  dispose(): void;
}

export function buildCrashFx(): CrashFx {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const mesh = <G extends BufferGeometry>(geometry: G, material: Material, parent: Object3D) => {
    geometries.push(geometry);
    const result = new Mesh(geometry, material);
    parent.add(result);
    return result;
  };

  const smoke = new Group();
  smoke.name = 'player.smoke';
  const grey = paint(0x8a8d92);
  [
    [-0.5, 0.1, 0.28],
    [-0.9, 0.35, 0.36],
    [-1.35, 0.7, 0.44],
  ].forEach(([x, y, radius]) => mesh(new IcosahedronGeometry(radius, 0), grey, smoke).position.set(x!, y!, 0));

  const pilot = new Group();
  pilot.name = 'player.pilot';
  mesh(new BoxGeometry(0.22, 0.32, 0.16), paint(LIVERY.blue), pilot);
  mesh(new SphereGeometry(0.11, 6, 4), paint(0xe0b48c), pilot).position.y = 0.26;
  mesh(new SphereGeometry(0.125, 6, 3, 0, Math.PI * 2, 0, Math.PI / 2), paint(LIVERY.yellow), pilot).position.y = 0.29;

  const parachute = new Group();
  parachute.name = 'player.parachute';
  mesh(new SphereGeometry(0.75, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), paint(LIVERY.red), parachute).position.y = 0.55;
  const rope = paint(LIVERY.rope);
  for (const x of [-0.45, 0.45]) {
    const line = mesh(new CylinderGeometry(0.012, 0.012, 0.7, 3), rope, parachute);
    line.position.set(x * 0.6, 0.2, 0);
    line.rotation.z = x > 0 ? -0.35 : 0.35;
  }

  for (const group of [smoke, pilot, parachute]) group.visible = false;
  return {
    smoke,
    pilot,
    parachute,
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
